import { useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import {
  driverIssueFieldConfiguration,
  driverIssueInitialValues,
} from "../../../formConfigs/Recruiting/driverIssueFieldConfig";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const fetchDrivers = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/getAllDrivers`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data;
};

const fetchTrucks = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrucks`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data;
};

const getUnitNumber = (fieldValue: any) => {
  if (
    fieldValue &&
    typeof fieldValue === "object" &&
    "unitNumber" in fieldValue
  ) {
    return fieldValue.unitNumber;
  }
  return undefined;
};

const getDriverName = (fieldValue: any) => {
  if (fieldValue && typeof fieldValue === "object" && "label" in fieldValue) {
    return fieldValue.label;
  }
  if (typeof fieldValue === "string") {
    return fieldValue;
  }
  return "";
};

const DriverIssuesPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchDrivers(token),
    enabled: open,
  });

  const { data: trucks = [] } = useQuery({
    queryKey: ["trucks"],
    queryFn: () => fetchTrucks(token),
    enabled: open,
  });

  const autocompleteData = useMemo(
    () => ({
      drivers: (drivers as any[]).map((driver) => ({
        id: driver.id?.toString?.() ?? driver.id,
        label: `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim(),
      })),
      trucks: (trucks as any[]).map((truck) => ({
        id: truck.id?.toString?.() ?? truck.id,
        label: `${truck.unitNumber ?? ""} | ${truck.make ?? ""} ${
          truck.model ?? ""
        } ${truck.year ?? ""}`.trim(),
        unitNumber: truck.unitNumber ?? "",
      })),
    }),
    [drivers, trucks]
  );

  const driverIssueMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/recruiting/driver-issue`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["driverIssues"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit driver issue");
    },
  });

  const submitHandler = (data: any) => {
    const driverId = getId(data.driverId);
    const truckId = getId(data.truckId);

    driverIssueMutation.mutate({
      ...data,
      driverId,
      truckId,
      driverName: getDriverName(data.driverId),
      truckUnitNumber: getUnitNumber(data.truckId),
      resolutionDate: data.resolutionDate || null,
    });
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Create Driver Issue
      </Button>
      <GenericFormPopup
        formFields={driverIssueFieldConfiguration}
        initialValues={driverIssueInitialValues}
        title="Driver Issue"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={autocompleteData}
        loading={driverIssueMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Driver issue created successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default DriverIssuesPopup;
